import React from 'react';
import { notification } from 'tea-component';
import type {
  IReportPermissionTarget,
  TPermissionLevel,
} from '@op/services/report';

type TFormatMessage = (
  descriptor: { id: string; },
  values?: Record<string, React.ReactNode>,
) => string;

export function renderPermissionLink(onClick: () => void, label: string) {
  return (
    <span
      className="dbo-report-permission-notify__link"
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }
      }}
    >
      {label}
    </span>
  );
}

const PERMISSION_LEVEL_NOTIFY_KEY: Record<TPermissionLevel, string> = {
  private:
    'Report visibility has been set to "Visible only to me"; click {permission} to adjust.',
  partial:
    'Report visibility has been set to "Visible to selected users"; click {permission} to adjust.',
  public:
    'Report visibility has been set to "Visible to everyone"; click {permission} to adjust.',
};

export function showReportPermissionAdjustedNotification({
  formatMessage,
  permissionLevel,
  onPermissionClick,
}: {
  formatMessage: TFormatMessage;
  permissionLevel: TPermissionLevel;
  onPermissionClick: () => void;
}) {
  const permissionLabel = formatMessage({ id: 'Permissions' });

  notification.success({
    description: (
      <span className="dbo-report-permission-notify">
        {formatMessage(
          { id: PERMISSION_LEVEL_NOTIFY_KEY[permissionLevel] },
          {
            permission: renderPermissionLink(
              onPermissionClick,
              permissionLabel,
            ),
          },
        )}
      </span>
    ),
  });
}

export function showReportPermissionUpdateSuccessNotification({
  formatMessage,
}: {
  formatMessage: TFormatMessage;
}) {
  notification.success({
    description: formatMessage({
      id: 'Report permissions have been successfully adjusted.',
    }),
  });
}

export function parseReportPermissionTarget(
  data?: {
    object_type?: string;
    object_id?: number | string | null;
  } | null,
): IReportPermissionTarget | null {
  if (
    !data?.object_type ||
    data.object_id === null ||
    data.object_id === undefined
  ) {
    return null;
  }

  return {
    object_type: data.object_type,
    object_id: Number(data.object_id),
  };
}

export function buildPermissionTarget(
  objectType: string,
  objectId: number | string,
): IReportPermissionTarget {
  return {
    object_type: objectType,
    object_id: Number(objectId),
  };
}
